// Home Economics Curriculum Data - Textile & Garment Construction and Food & Nutrition
// Based on the Vanuatu National Curriculum for Years 7-10

export const homeEconomicsCurriculum = {
  "Year 9": {
    textileGarment: {
      name: "Textile and Garment Construction",
      strands: [
        {
          id: 1,
          name: "Design Understanding",
          color: "bg-purple-100 text-purple-800",
          weight: 12,
          topics: [
            "PRISME procedure in textile production",
            "Impacts of design process on products",
            "Closed design briefs",
            "Manufacturing requirements",
            "Object construction and analysis"
          ],
          slos: [
            "State factors affecting product if design process not applied",
            "Describe impacts of using design process to solve design brief",
            "Construct three objects and analyse results",
            "Define closed design brief",
            "Explain importance of closed design brief in garment manufacturing"
          ]
        },
        {
          id: 2,
          name: "Clothing and Sewing Processes",
          color: "bg-pink-100 text-pink-800",
          weight: 36,
          topics: [
            "Man-made fibres and natural products",
            "Fabric production from natural products",
            "Sewing machines in Vanuatu",
            "OHS safety rules for sewing room",
            "Pressing tools and sewing notions"
          ],
          slos: [
            "Define man-made fibre",
            "List natural products used to produce man-made fibre",
            "Draw and label natural products for fabric production",
            "Name available sewing machines",
            "List all OHS safety rules for sewing room and equipment",
            "Name pressing tools and define sewing notions"
          ]
        },
        {
          id: 3,
          name: "Clothing Needs",
          color: "bg-amber-100 text-amber-800",
          weight: 12,
          topics: [
            "Traditional clothes and materials",
            "Production of traditional clothes",
            "Preserving traditional values",
            "Western clothing repair and mending",
            "Clothing recycling techniques"
          ],
          slos: [
            "Name traditional clothes and list materials",
            "Investigate production of traditional clothes",
            "Explain importance of preserving traditional values",
            "Define western clothing, repair and mending",
            "List ways of repairing western clothing",
            "Repair western clothing"
          ]
        }
      ],
      assessments: [
        { name: "Internal Examination (IE)", weight: 60, description: "Written exam administered mid-year and end-of-year" },
        { name: "Continuous Assessment (CA)", weight: 40, description: "Practical tasks and projects throughout the year" }
      ]
    },
    foodNutrition: {
      name: "Food and Nutrition",
      strands: [
        {
          id: 1,
          name: "Design Understanding",
          color: "bg-blue-100 text-blue-800",
          weight: 12,
          topics: [
            "PRISME procedure in food production",
            "Design briefs - purpose and application",
            "Concept development and selection",
            "Object creation and analysis",
            "Design process importance"
          ],
          slos: [
            "Demonstrate understanding of PRISME procedure in food production",
            "Discuss importance of design process in food product construction",
            "Apply design brief in real situations",
            "Create objects and criticize shapes",
            "Analyse results of design process"
          ]
        },
        {
          id: 2,
          name: "Food Choices",
          color: "bg-green-100 text-green-800",
          weight: 24,
          topics: [
            "Food groups and their importance",
            "Nutrients and healthy diets",
            "Protein foods (HBV and LBV)",
            "Cereals - types and uses",
            "Food preservation (modern and traditional)",
            "Energy balance",
            "Staple foods of Vanuatu"
          ],
          slos: [
            "Demonstrate understanding of food groups importance",
            "Investigate reasons for colours used in each food group",
            "Discuss percentage rates between food groups",
            "Understand different protein foods (HBV and LBV)",
            "Plan, prepare, cook and serve cereal dishes",
            "Investigate effects of nutrient deficiency"
          ]
        },
        {
          id: 3,
          name: "Food Preparation Skills",
          color: "bg-orange-100 text-orange-800",
          weight: 24,
          topics: [
            "Kitchen safety and hygiene (OHS)",
            "First aid basics",
            "Kitchen equipment and utensils",
            "Food preparation methods",
            "Cooking methods (baking, cakes)",
            "Dish evaluation"
          ],
          slos: [
            "Demonstrate understanding of hygiene and safety routines",
            "Discuss kitchen and personal hygiene",
            "Apply kitchen equipment and utensils to produce dishes",
            "Apply correct skills in dish preparation",
            "Produce dishes using various cooking methods",
            "Evaluate completed dishes"
          ]
        }
      ],
      assessments: [
        { name: "Internal Examination (IE)", weight: 60, description: "Two-hour written exam - mid-year and end-of-year" },
        { name: "Continuous Assessment (CA)", weight: 40, description: "Teacher Design Tasks (TDT) and Common Assessment Task (CAT)" }
      ]
    }
  },
  // Year level progression overview
  progression: {
    "Year 7": {
      textile: "Basic PRISME procedure, simple sewing equipment, traditional wear understanding",
      food: "Introduction to food groups, basic kitchen safety, simple meal preparation"
    },
    "Year 8": {
      textile: "Complex sewing materials, laundry techniques, stain removal",
      food: "Nutrients understanding, food preservation basics, kitchen equipment"
    },
    "Year 9": {
      textile: "Advanced PRISME application, OHS rules, traditional clothes production, repair techniques",
      food: "Protein foods, cereals, cooking methods, energy balance, advanced food preparation"
    },
    "Year 10": {
      textile: "Complex garment making, recycling techniques, advanced design application",
      food: "Advanced nutrition, meal planning, complex cooking techniques, food business concepts"
    }
  }
};

export const homeEconomicsSchemeOfWork = {
  "Year 9": {
    term1: {
      weeks: [
        { week: 1, topic: "Course Introduction", subject: "Both", activities: "Overview of Year 9 content, safety rules, expectations" },
        { week: 2, topic: "PRISME Procedure Review", subject: "Both", activities: "Understanding design process for textiles and food" },
        { week: 3, topic: "Design Briefs", subject: "Both", activities: "Closed design briefs, application in real situations" },
        { week: 4, topic: "Man-made Fibres", subject: "Textile", activities: "Definitions, natural products for fabric production" },
        { week: 5, topic: "Food Groups", subject: "Food", activities: "Understanding food groups, colours, percentages" },
        { week: 6, topic: "Sewing Machines", subject: "Textile", activities: "Types available in Vanuatu, basic operation" },
        { week: 7, topic: "Nutrients", subject: "Food", activities: "Six nutrients, importance in diet" },
        { week: 8, topic: "OHS Safety Rules", subject: "Both", activities: "Sewing room and kitchen safety" },
        { week: 9, topic: "Term 1 Assessment", subject: "Both", activities: "Practical tasks and written test" },
        { week: 10, topic: "Review", subject: "Both", activities: "Term review, feedback" }
      ]
    },
    term2: {
      weeks: [
        { week: 1, topic: "Pressing Tools & Notions", subject: "Textile", activities: "Types, uses, definitions" },
        { week: 2, topic: "Protein Foods", subject: "Food", activities: "HBV and LBV proteins, sources" },
        { week: 3, topic: "Traditional Clothes", subject: "Textile", activities: "Materials, production methods" },
        { week: 4, topic: "Cereals", subject: "Food", activities: "Types, composition, preparation" },
        { week: 5, topic: "Traditional Values", subject: "Textile", activities: "Preserving cultural heritage in clothing" },
        { week: 6, topic: "Cereal Dish Practical", subject: "Food", activities: "Plan, prepare, cook, serve, evaluate" },
        { week: 7, topic: "Mid-Year Exam Review", subject: "Both", activities: "Revision for mid-year examination" },
        { week: 8, topic: "Mid-Year Examination", subject: "Both", activities: "Written examination (IE)" },
        { week: 9, topic: "Practical Assessment", subject: "Both", activities: "Continuous assessment tasks" },
        { week: 10, topic: "Review", subject: "Both", activities: "Term review, feedback" }
      ]
    },
    term3: {
      weeks: [
        { week: 1, topic: "Western Clothing", subject: "Textile", activities: "Definition, repair and mending" },
        { week: 2, topic: "Food Preservation", subject: "Food", activities: "Modern and traditional methods" },
        { week: 3, topic: "Repair Techniques", subject: "Textile", activities: "Practical repair of western clothing" },
        { week: 4, topic: "Kitchen Equipment", subject: "Food", activities: "Parts, uses, application in cooking" },
        { week: 5, topic: "Object Construction", subject: "Textile", activities: "Create objects, analyse results" },
        { week: 6, topic: "Energy Balance", subject: "Food", activities: "Understanding dietary energy needs" },
        { week: 7, topic: "Staple Foods of Vanuatu", subject: "Food", activities: "Local foods, nutrition value" },
        { week: 8, topic: "Cooking Methods", subject: "Food", activities: "Baking, cakes, practical cooking" },
        { week: 9, topic: "End of Year Review", subject: "Both", activities: "Comprehensive revision" },
        { week: 10, topic: "Final Assessment", subject: "Both", activities: "End-of-year examination and practical" }
      ]
    },
    term4: {
      weeks: [
        { week: 1, topic: "Advanced Design Application", subject: "Both", activities: "Applying PRISME to complete projects" },
        { week: 2, topic: "Project Work", subject: "Textile", activities: "Complete garment construction project" },
        { week: 3, topic: "Project Work", subject: "Food", activities: "Complete meal preparation project" },
        { week: 4, topic: "Project Work", subject: "Both", activities: "Continue project work" },
        { week: 5, topic: "Project Evaluation", subject: "Both", activities: "Evaluate completed projects" },
        { week: 6, topic: "Portfolio Compilation", subject: "Both", activities: "Compile work samples and reflections" },
        { week: 7, topic: "Final Practical Assessment", subject: "Both", activities: "Final CA tasks" },
        { week: 8, topic: "End of Year Examination", subject: "Both", activities: "Written examination (IE)" },
        { week: 9, topic: "Results and Feedback", subject: "Both", activities: "Assessment feedback, goal setting" },
        { week: 10, topic: "Course Conclusion", subject: "Both", activities: "Year wrap-up, preparation for Year 10" }
      ]
    }
  }
};

// Teacher-specific resources
export const homeEconomicsTeacherResources = {
  'teacher-9346c059': [ // Trevina Tari - Junior St Patrick's College
    {
      id: 'textile-syllabus-y9',
      name: 'Year 9 Textile and Garment Construction Syllabus',
      description: 'Complete syllabus with SLOs for Year 9 Textile and Garment Construction',
      url: 'https://customer-assets.emergentagent.com/job_fef6b712-b8fc-4b72-95db-2ec6aee6123b/artifacts/kj6n9ysn_Y9_Syl_Textile%20and%20Garment%20Construction_FF_RevRR14July2025_Aug2025.pdf',
      type: 'pdf',
      year: 'Year 9',
      subject: 'Textile & Garment'
    },
    {
      id: 'food-syllabus-y9',
      name: 'Year 9 Food and Nutrition Syllabus',
      description: 'Complete syllabus with SLOs for Year 9 Food and Nutrition',
      url: 'https://customer-assets.emergentagent.com/job_fef6b712-b8fc-4b72-95db-2ec6aee6123b/artifacts/lukox495_Y9_SYL_Food%20and%20Nutrition_FF_RevRR14July2025_Aug2025_RevRRp8Sept25.pdf',
      type: 'pdf',
      year: 'Year 9',
      subject: 'Food & Nutrition'
    }
  ]
};
